import { DateTime } from 'luxon';

/** Default first day of week. ISO 8601: 1 = Monday … 7 = Sunday. */
export const DEFAULT_FIRST_DAY_OF_WEEK = 1;

/**
 * Computes the start and end boundaries of the rota week containing
 * `dateStr` (`YYYY-MM-DD`). The week starts on `firstDayOfWeek`
 * (ISO 8601: 1 = Monday … 7 = Sunday) and spans 7 consecutive days.
 *
 * Returns `null` for empty input or an unparseable date string.
 * Boundaries come back as `YYYY-MM-DD` strings so consumers can
 * round-trip them through `<input type="date">` without timezone
 * drift.
 */
export function getWeekBoundaries(
  dateStr: string | null,
  firstDayOfWeek: number = DEFAULT_FIRST_DAY_OF_WEEK,
): { weekStart: string; weekEnd: string } | null {
  if (!dateStr) return null;
  const date = DateTime.fromISO(dateStr);
  if (!date.isValid) return null;
  // Luxon `weekday` is ISO 1..7 — matches `firstDayOfWeek` directly.
  const offsetToStart = (date.weekday - firstDayOfWeek + 7) % 7;
  const weekStart = date.minus({ days: offsetToStart });
  const weekEnd = weekStart.plus({ days: 6 });
  const startStr = weekStart.toISODate();
  const endStr = weekEnd.toISODate();
  if (!startStr || !endStr) return null;
  return { weekStart: startStr, weekEnd: endStr };
}

/**
 * Returns the start of next week as `YYYY-MM-DD`. The week starts on
 * `firstDayOfWeek` (ISO 8601: 1 = Monday … 7 = Sunday). When today
 * already IS the start of a week, jumps to the *following* week
 * (the modulo-then-`|| 7` trick).
 */
export function getNextWeekStart(firstDayOfWeek: number = DEFAULT_FIRST_DAY_OF_WEEK): string {
  const today = DateTime.now();
  const daysUntilNext = (firstDayOfWeek - today.weekday + 7) % 7 || 7;
  const next = today.plus({ days: daysUntilNext });
  // `next` is derived from `DateTime.now()` plus a positive integer day
  // count — always valid, so `toISODate()` is non-null. The fallback
  // exists only to satisfy the `string | null` return type from Luxon.
  return next.toISODate() ?? '';
}

/**
 * Formats a `YYYY-MM-DD` date string as `DD/MM/YYYY` for display.
 * Returns an empty string for unparseable input so consumers don't
 * have to handle nulls inline.
 */
export function formatDateDMY(dateStr: string): string {
  const date = DateTime.fromISO(dateStr);
  return date.isValid ? date.toFormat('dd/MM/yyyy') : '';
}

/**
 * Returns the display label for an ISO day number (1 = Monday …
 * 7 = Sunday). Falls back to `'Monday'` for out-of-range values.
 *
 * Locale-derived via Luxon: labels are computed once at module load
 * from a known Monday anchor, so the output respects the runtime
 * default locale today and will follow Lingui's active locale once
 * the retrofit (#97) chains `setLocale(...)` onto the DateTime.
 */
export function getDayLabel(isoDayNumber: number): string {
  return DAY_NAMES_BY_ISO[isoDayNumber] ?? 'Monday';
}

/**
 * Returns the ISO day number of the last day in a week that starts
 * on `firstDayOfWeek`. E.g. Monday-start (1) → Sunday end (7);
 * Sunday-start (7) → Saturday end (6).
 */
export function getEndDayIso(firstDayOfWeek: number): number {
  return firstDayOfWeek === 1 ? 7 : firstDayOfWeek - 1;
}

/**
 * Adds one calendar day to a `YYYY-MM-DD` date string. Used when
 * converting a UI inclusive-end date to the half-open interval that
 * rotagen's `/v1/generations` endpoint expects (`weekEnd + 1 day` →
 * exclusive end).
 *
 * Returns an empty string for unparseable input so the caller can
 * decide how to handle it (the rotagen call will reject an empty
 * range explicitly rather than silently doing the wrong thing).
 */
export function addOneDayISO(dateStr: string): string {
  const date = DateTime.fromISO(dateStr);
  if (!date.isValid) return '';
  return date.plus({ days: 1 }).toISODate() ?? '';
}

/**
 * Enumerates every calendar day in the inclusive `YYYY-MM-DD` range
 * `start`..`end`, returning each day's ISO date alongside its ISO weekday
 * (1 = Monday … 7 = Sunday). Used to key a per-day demand forecast by date
 * while resolving the right Mon–Sun baseline figure for each day.
 *
 * Returns an empty array for unparseable input or an inverted range.
 */
export function enumerateWeekDays(
  start: string,
  end: string,
): { iso: string; isoWeekday: number }[] {
  const from = DateTime.fromISO(start);
  const to = DateTime.fromISO(end);
  if (!from.isValid || !to.isValid || to < from) return [];

  const days: { iso: string; isoWeekday: number }[] = [];
  for (let cursor = from; cursor <= to; cursor = cursor.plus({ days: 1 })) {
    const iso = cursor.toISODate();
    if (iso) days.push({ iso, isoWeekday: cursor.weekday });
  }
  return days;
}

/**
 * ISO weekday → long name lookup. Built once at module load from
 * a known Monday anchor (2024-01-01) so future Lingui swaps can
 * rebuild the table with the active locale without touching
 * consumers.
 */
const DAY_NAMES_BY_ISO: Record<number, string> = (() => {
  const monday = DateTime.fromISO('2024-01-01');
  const result: Record<number, string> = {};
  for (let i = 0; i < 7; i++) {
    const d = monday.plus({ days: i });
    result[d.weekday] = d.toFormat('cccc');
  }
  return result;
})();
